/**
 * BOSS avans kararını değiştirme modalı.
 * onaylandi ↔ reddedildi ↔ karsiTeklif (ödenmemiş için).
 */

import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { AlertTriangle, CheckCircle2, XCircle, RefreshCw } from 'lucide-react'
import { avansKararDegistir, type Avans } from '@/lib/services/avans-service'
import { useAuth } from '@/contexts/AuthContext'
import type { KullaniciPersonel } from '@/types'

interface Props {
  acik: boolean
  avans: Avans | null
  personel: KullaniciPersonel | null
  onKapat: () => void
  onKaydedildi: () => void
}

type Secim = null | 'onay' | 'red' | 'karsiTeklif'

export function AvansKararDegistirModal({ acik, avans, personel, onKapat, onKaydedildi }: Props) {
  const { firebaseUser, kullanici } = useAuth()
  const [secim, setSecim] = useState<Secim>(null)
  const [redSebebi, setRedSebebi] = useState('')
  const [karsiTeklifTutar, setKarsiTeklifTutar] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [kaydediliyor, setKaydediliyor] = useState(false)

  useEffect(() => {
    if (!acik) return
    setSecim(null)
    setRedSebebi('')
    setKarsiTeklifTutar('')
    setHata(null)
  }, [acik])

  if (!avans || !personel) return null
  if (avans.durum !== 'onaylandi' && avans.durum !== 'reddedildi') return null

  const tersDurum: 'onaylandi' | 'reddedildi' =
    avans.durum === 'onaylandi' ? 'reddedildi' : 'onaylandi'
  const tersOnay = tersDurum === 'onaylandi'
  const onayTutar = avans.karsiTeklifTutari ?? avans.talepTutari

  async function kaydet(yeniDurum: 'onaylandi' | 'reddedildi' | 'karsiTeklif') {
    if (!firebaseUser || !avans) return
    setKaydediliyor(true)
    setHata(null)
    try {
      const opsiyonlar: {
        redSebebi?: string
        karsiTeklifTutari?: number
        yapanIsim?: string
      } = { yapanIsim: kullanici?.isim }

      if (yeniDurum === 'reddedildi') {
        opsiyonlar.redSebebi = redSebebi
      } else if (yeniDurum === 'karsiTeklif') {
        const sayi = parseFloat(karsiTeklifTutar)
        if (isNaN(sayi) || sayi <= 0) {
          setHata('Geçerli bir tutar gir.')
          setKaydediliyor(false)
          return
        }
        if (sayi >= avans.talepTutari) {
          setHata('Karşı teklif, talep tutarından düşük olmalı.')
          setKaydediliyor(false)
          return
        }
        opsiyonlar.karsiTeklifTutari = sayi
      }

      await avansKararDegistir(avans.id, yeniDurum, firebaseUser.uid, opsiyonlar)
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  const mevcutEtiket = avans.durum === 'onaylandi'
    ? `Onaylanmış (${onayTutar.toLocaleString('de-DE')} ₺)`
    : 'Reddedilmiş'

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Kararı Değiştir" boyut="md">
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">{personel.isim} — Mevcut: {mevcutEtiket}</p>
            <p className="text-xs mt-1">
              Talep tutarı: {avans.talepTutari.toLocaleString('de-DE')} ₺
              {avans.aciklama && <span className="italic"> — "{avans.aciklama}"</span>}
            </p>
          </div>
        </div>

        {!secim && (
          <>
            <p className="text-sm text-gray-700 font-medium">Yeni karar:</p>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => setSecim(tersOnay ? 'onay' : 'red')}
                className={`p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                  tersOnay
                    ? 'border-green-200 bg-green-50 text-green-700 hover:border-green-400'
                    : 'border-red-200 bg-red-50 text-red-700 hover:border-red-400'
                }`}
              >
                {tersOnay ? <CheckCircle2 className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm">{tersOnay ? 'Onayla' : 'Reddet'}</p>
                  <p className="text-xs">
                    {tersOnay
                      ? `${onayTutar.toLocaleString('de-DE')} ₺ onaylanır`
                      : 'Talebi reddet (sebep yazabilirsin)'}
                  </p>
                </div>
              </button>

              <button
                onClick={() => setSecim('karsiTeklif')}
                className="p-3 rounded-xl border-2 border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-400 flex items-center gap-3 transition-colors"
              >
                <RefreshCw className="w-6 h-6" />
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm">Karşı Teklif</p>
                  <p className="text-xs">Daha düşük bir tutar öner — personel kabul/red eder</p>
                </div>
              </button>
            </div>
            <div className="pt-2">
              <button onClick={onKapat} className="btn-secondary w-full" disabled={kaydediliyor}>
                Vazgeç
              </button>
            </div>
          </>
        )}

        {secim === 'onay' && (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
              <p className="font-bold text-green-900">
                {onayTutar.toLocaleString('de-DE')} ₺ onaylanacak
              </p>
              <p className="text-xs text-green-700 mt-1">
                Red kararı kaldırılır. Avanslar sayfasında "Ödendi" işaretleyince ay sonu
                maaşından düşer.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setSecim(null)} className="btn-secondary flex-1" disabled={kaydediliyor}>
                Geri
              </button>
              <button
                onClick={() => kaydet('onaylandi')}
                disabled={kaydediliyor}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl"
              >
                {kaydediliyor ? 'Kaydediliyor...' : 'Onayla'}
              </button>
            </div>
          </div>
        )}

        {secim === 'red' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Red Sebebi (opsiyonel)
              </label>
              <textarea
                rows={3}
                value={redSebebi}
                onChange={(e) => setRedSebebi(e.target.value)}
                className="input-base"
                placeholder="Personele görünecek..."
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setSecim(null)} className="btn-secondary flex-1" disabled={kaydediliyor}>
                Geri
              </button>
              <button
                onClick={() => kaydet('reddedildi')}
                disabled={kaydediliyor}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl"
              >
                {kaydediliyor ? 'Kaydediliyor...' : 'Reddet'}
              </button>
            </div>
          </div>
        )}

        {secim === 'karsiTeklif' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Karşı Teklif Tutarı (₺) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max={avans.talepTutari - 1}
                step="any"
                value={karsiTeklifTutar}
                onChange={(e) => setKarsiTeklifTutar(e.target.value)}
                className="input-base text-lg font-semibold"
                placeholder="0"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Talep edilen {avans.talepTutari.toLocaleString('de-DE')} ₺'den düşük olmalı.
              </p>
            </div>
            <p className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-2">
              💡 Önceki karar geri alınır, personele yeni teklif gider. Kabul ederse
              onaylanmış olur, reddederse talep reddedilmiş kalır.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setSecim(null)} className="btn-secondary flex-1" disabled={kaydediliyor}>
                Geri
              </button>
              <button
                onClick={() => kaydet('karsiTeklif')}
                disabled={kaydediliyor}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 rounded-xl"
              >
                {kaydediliyor ? 'Gönderiliyor...' : 'Karşı Teklif Gönder'}
              </button>
            </div>
          </div>
        )}

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}
      </div>
    </Modal>
  )
}
